import { getApiUrl } from "./config";
import { ApiError, requestJson } from "./requestJson";
import { requestTurnstileToken } from "./turnstile";

const BORROWED_ACCESS_STORAGE_KEY = "travelJournalBorrowedAccessToken.v1";
const LEGACY_API_KEY_STORAGE_KEY = "openai_api_key";
const DEFAULT_TOKEN_LIFETIME_MS = 24 * 60 * 60 * 1000;
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

type BorrowedAccessResponse = { api_key: string };
type StoredBorrowedAccess = { token: string; expiresAt: number };

let pendingAccessToken: Promise<string> | null = null;

export function borrowAccessToken(
  signal?: AbortSignal,
  getTurnstileToken: (signal?: AbortSignal) => Promise<string> = requestTurnstileToken,
): Promise<string> {
  localStorage.removeItem(LEGACY_API_KEY_STORAGE_KEY);
  const stored = readStoredAccess();
  if (stored && stored.expiresAt > Date.now() + REFRESH_BEFORE_EXPIRY_MS) {
    return Promise.resolve(stored.token);
  }
  if (pendingAccessToken) return pendingAccessToken;

  pendingAccessToken = obtainAccessToken(stored, signal, getTurnstileToken).finally(() => {
    pendingAccessToken = null;
  });
  return pendingAccessToken;
}

export function invalidateAccessToken(token?: string): void {
  const stored = readStoredAccess();
  if (!token || stored?.token === token) {
    sessionStorage.removeItem(BORROWED_ACCESS_STORAGE_KEY);
  }
}

async function obtainAccessToken(
  stored: StoredBorrowedAccess | null,
  signal: AbortSignal | undefined,
  getTurnstileToken: (signal?: AbortSignal) => Promise<string>,
): Promise<string> {
  if (stored) {
    const refreshedToken = await refreshAccessToken(stored.token, signal);
    if (refreshedToken) return storeAccessToken(refreshedToken);
    sessionStorage.removeItem(BORROWED_ACCESS_STORAGE_KEY);
  }

  const turnstileToken = await getTurnstileToken(signal);
  const response = await requestJson(getApiUrl("/borrowApiKey"), {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ turnstile_token: turnstileToken }),
    mode: "cors",
    signal,
    timeoutMs: 20_000,
    validate: isBorrowedAccessResponse,
  });
  return storeAccessToken(response.api_key);
}

async function refreshAccessToken(token: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const response = await requestJson(getApiUrl("/refreshApiKey"), {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      mode: "cors",
      signal,
      timeoutMs: 20_000,
      validate: isBorrowedAccessResponse,
    });
    return response.api_key;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

function storeAccessToken(token: string): string {
  sessionStorage.setItem(
    BORROWED_ACCESS_STORAGE_KEY,
    JSON.stringify({
      token,
      expiresAt: readJwtExpiration(token) ?? Date.now() + DEFAULT_TOKEN_LIFETIME_MS,
    } satisfies StoredBorrowedAccess),
  );
  return token;
}

function readStoredAccess(): StoredBorrowedAccess | null {
  const storedValue = sessionStorage.getItem(BORROWED_ACCESS_STORAGE_KEY);
  if (!storedValue) return null;
  try {
    const value = JSON.parse(storedValue) as unknown;
    if (!isStoredBorrowedAccess(value)) throw new Error("Invalid stored access token");
    return value;
  } catch {
    sessionStorage.removeItem(BORROWED_ACCESS_STORAGE_KEY);
    return null;
  }
}

function isBorrowedAccessResponse(value: unknown): value is BorrowedAccessResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Record<string, unknown>).api_key === "string"
  );
}

function isStoredBorrowedAccess(value: unknown): value is StoredBorrowedAccess {
  if (typeof value !== "object" || value === null) return false;
  const access = value as Record<string, unknown>;
  return typeof access.token === "string" && typeof access.expiresAt === "number";
}

function readJwtExpiration(token: string): number | null {
  const payload = token.startsWith("skt-") ? token.slice(4).split(".")[1] : null;
  if (!payload) return null;
  try {
    const normalized = payload.replaceAll("-", "+").replaceAll("_", "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const claims = JSON.parse(atob(padded)) as unknown;
    if (typeof claims !== "object" || claims === null) return null;
    const expiration = (claims as Record<string, unknown>).exp;
    return typeof expiration === "number" ? expiration * 1000 : null;
  } catch {
    return null;
  }
}
